import React, { useEffect } from 'react';
import BrandInfoProvider from '@core-ui/brand-info-provider';
import { AppState, AppStateProvider } from '@core-ui/app-state-provider';
import { Provider as FeatureFlagsProvider } from '@core-ui/feature-flags';
import { BreakpointProvider } from '@core-ui/breakpoint-provider';
import { StitchStyleProvider } from '@core-ui/react-stitch';
import { LocalizationProvider } from '@core-ui/localization-provider';
import { PersonalizationProvider } from '@core-ui/personalization-provider';
import { SitewideContextProvider } from '@mfe/sitewide';
import { PageState, ErrorLogger } from '../types/app';
import { PageDataProvider } from './providers/page-data-provider';
import { CustomLandingPage } from './components/custom-landing-page';
import { BopisWrapper } from './components/bopis-wrapper';

export interface AppProps extends PageState, AppState {
  errorLogger: ErrorLogger
}

const App = (props: AppProps): JSX.Element => {
  const {
    brand,
    brandInfo,
    breadcrumbs,
    channel,
    copyblock,
    datalayer,
    enabledFeatures = {},
    errorLogger,
    featureVariables = {},
    initialMedia,
    initialSizeClass,
    locale,
    market,
    page_name,
    page_type,
    pageType,
    rawThematicApiData,
    theme,
    translations,
  } = props;

  useEffect(() => {
    datalayer.add({
      name: 'custom-landing-page',
      data: {
        channel,
        page_name,
        page_type,
      },
    });
  }, [page_name, page_type, channel]);

  return (
    <AppStateProvider value={{ ...props, brandName: brandInfo.abbrBrand }}>
      <SitewideContextProvider>
        <FeatureFlagsProvider value={{ enabledFeatures, featureVariables }}>
          <BreakpointProvider initialMedia={initialMedia} initialSizeClass={initialSizeClass}>
            <LocalizationProvider locale={locale} translations={translations}>
              <BrandInfoProvider
                abbrBrand={brandInfo.abbrBrand}
                brand={brand}
                brandCode={brandInfo.marketAwareBrandCode}
              >
                <StitchStyleProvider brand={brandInfo.abbrBrand} enabledFeatures={enabledFeatures}>
                  <PageDataProvider
                    brand={brand}
                    breadcrumbs={breadcrumbs}
                    copyblock={copyblock}
                    enabledFeatures={enabledFeatures}
                    market={market}
                    rawThematicApiData={rawThematicApiData}
                    theme={theme}
                  >
                    <PersonalizationProvider
                      datalayer={datalayer}
                      errorLogger={errorLogger}
                      marketAgnosticBrandCode={brandInfo.marketAgnosticBrandCode}
                    >
                      <BopisWrapper
                        brand={brand}
                        market={market}
                        pageType={pageType}
                        errorLogger={errorLogger}
                      >
                        <CustomLandingPage />
                      </BopisWrapper>
                    </PersonalizationProvider>
                  </PageDataProvider>
                </StitchStyleProvider>
              </BrandInfoProvider>
            </LocalizationProvider>
          </BreakpointProvider>
        </FeatureFlagsProvider>
      </SitewideContextProvider>
    </AppStateProvider>
  );
};

export default App;
